import {useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {FaEye, FaEyeSlash} from 'react-icons/fa'
import {registerUser} from '../../../../api'
import countries from '../../../../utils/constants'

type UserData = {
  name: string
  ph: string
  email: string
  password: string
  confirmPassword?: string
  country_code?: string
  role?: string
}

export function Registration() {
  const [userData, setUserData] = useState<UserData>({
    name: '',
    ph: '',
    email: '',
    password: '',
    confirmPassword: '',
    country_code: '',
    role:''
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmShowPassword] = useState(false)
  const [errors, setErrors] = useState<Partial<UserData>>({})
  const navigate = useNavigate()

  const validate = () => {
    const newErrors: Partial<UserData> = {}

    if (!userData.name.trim()) {
      newErrors.name = 'Username is required'
    }
    // if (userData.ph.length < 10 || !/^\d+$/.test(userData.ph)) {
    //   newErrors.ph = 'Enter a valid phone number'
    // }
    if (!userData.email.trim() || !/^[\w.%+-]+@mapmycrop\.com$/.test(userData.email)) {
      newErrors.email = 'Email must be in the format "example@mapmycrop.com"'
    }
    if(!userData.role){
      newErrors.role ='Role is required'
    }
    if (!userData.country_code) {
      newErrors.country_code = 'Country is required'
    }
    const passwordValidations = [
      {test: (pwd) => !!pwd, error: 'Password is required'},
      {test: (pwd) => pwd.length >= 8, error: 'Password must be at least 8 characters'},
      {
        test: (pwd) => /[A-Z]/.test(pwd),
        error: 'Password must contain at least one uppercase letter',
      },
      {
        test: (pwd) => /[a-z]/.test(pwd),
        error: 'Password must contain at least one lowercase letter',
      },
      {test: (pwd) => /\d/.test(pwd), error: 'Password must contain at least one number'},
      {
        test: (pwd) => /[@$!%*?&#^(){}[\]/\\.,<>;:'"`~|_+=-]/.test(pwd),
        error: 'Password must contain at least one special character',
      },
    ]

    const passwordError = passwordValidations.find(
      (validation) => !validation.test(userData.password)
    )

    if (passwordError) {
      newErrors.password = passwordError.error
    }

    if (!userData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required'
    } else if (userData.confirmPassword !== userData.password) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    const selectedCountry = countries.find((country) => country.code === userData.country_code)
    if (!selectedCountry) {
      newErrors.country_code = 'Please select a valid country'
    }

    if (!userData.ph || !/^\d+$/.test(userData.ph)) {
      newErrors.ph = 'Phone number must contain only digits'
    } else if (selectedCountry && userData.ph.length !== selectedCountry.phoneNumberLength) {
      newErrors.ph = `Phone number must be ${selectedCountry.phoneNumberLength} digits for ${selectedCountry.name}`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setUserData({
      name: '',
      ph: '',
      email: '',
      country_code: '',
      password: '',
      confirmPassword: '',
      role:''
    })
    setErrors({})
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validate()) return
    try {
      const response = await registerUser(userData)
      if (response) {
        Swal.fire({
          title: 'Sign up successful! Redirecting to login...',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          resetForm()
          navigate('/auth')
        })
      }
    } catch (error: any) {
      console.error('Error registering user:', error.response.data.detail)
      Swal.fire({
        title: error.response.data.detail,
        icon: 'error',
        confirmButtonText: 'OK',
      })
    }
  }

  const handleChange = (value: string, fieldName: keyof UserData) => {
    setUserData((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  return (
    <div className='d-flex flex-column flex-root vh-100'>
      <div className='d-flex flex-column flex-lg-row h-100 w-100'>
        <div
          className='d-flex align-items-center justify-content-center w-100'
          style={{flex: '7', height: '100vh'}}
        >
          <img
            src='/assets/media/logos/background.jpg'
            alt='Login'
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
          />
        </div>
        <div
          className='d-flex flex-column justify-content-center align-items-center pt-2 pb-2 px-10 w-100'
          style={{flex: '3'}}
        >
          <div
            className='fixed'
            style={{
              top: '10%',
              right: '160px',
              zIndex: 1000,
              paddingBottom: '4%',
            }}
          >
            <img
              alt='logo'
              src='/assets/media/logos/mapmycrop.png'
              style={{
                width: '200px',
                height: '7vh',
              }}
            />
          </div>
          <div
            className='bg-body d-flex flex-column flex-center rounded-4 p-5 w-100'
            style={{maxWidth: '450px'}}
          >
            <form className='form w-100' onSubmit={handleSubmit}>
              <h1 className='text-dark fw-bolder mb-3 text-center'>Sign Up</h1>
              <div className='mb-5'>
                <input
                  type='text'
                  placeholder='Username'
                  value={userData.name}
                  onChange={(e) => handleChange(e.target.value, 'name')}
                  className='form-control bg-transparent'
                  required
                />

                {errors.name && <div className='text-danger mt-2'>{errors.name}</div>}
              </div>
              <div className='mb-5'>
                <select
                  className='form-control bg-transparent mb-2'
                  value={userData.country_code}
                  onChange={(e) => handleChange(e.target.value, 'country_code')}
                >
                  <option value='' disabled>
                    Select Country
                  </option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
                {errors.country_code && (
                  <div className='text-danger mt-2'>{errors.country_code}</div>
                )}
              </div>
              <div className='mb-5'>
                <input
                  type='text'
                  placeholder='Enter phone number'
                  value={userData.ph}
                  onChange={(e) => {
                    const value = e.target.value
                    if (/^\d*$/.test(value)) {
                      handleChange(value, 'ph')
                    }
                  }}
                  className='form-control bg-transparent'
                  required
                />
                {errors.ph && <div className='text-danger mt-2'>{errors.ph}</div>}
              </div>
              <div className='mb-5'>
                <input
                  type='email'
                  placeholder='Email'
                  value={userData.email}
                  onChange={(e) => handleChange(e.target.value, 'email')}
                  className='form-control bg-transparent'
                />
                {errors.email && <div className='text-danger mt-2'>{errors.email}</div>}
              </div>
              <div className='mb-5'> 
                 <select name='role' 
                 value={userData.role}
                 onChange={(e) => handleChange(e.target.value, 'role')}
                 className='form-control bg-transparent'
                 placeholder='Select Role'
                 >
                  <option value=''>Select Role</option>
                  <option value='Admin'>Admin</option>
                  <option value='Agronomist'>Agronomist</option>
                  <option value='Sales'>Sales</option>
                </select>
                {errors.role && <div className='text-danger mt-2'>{errors.role}</div>}
              </div>
              <div className='mb-5 position-relative'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Password'
                  value={userData.password}
                  onChange={(e) => handleChange(e.target.value, 'password')}
                  className='form-control bg-transparent'
                  required
                />
                <span
                  className='position-absolute top-50 translate-middle-y'
                  style={{right: '10px', cursor: 'pointer'}}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
                {errors.password && errors.password && (
                  <div className='text-danger mt-2'>{errors.password}</div>
                )}
              </div>
              <div className='mb-5 position-relative'>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder='Confirm Password'
                  value={userData.confirmPassword}
                  onChange={(e) => handleChange(e.target.value, 'confirmPassword')}
                  className='form-control bg-transparent'
                  required
                />
                <span
                  className='position-absolute top-50 translate-middle-y'
                  style={{right: '10px', cursor: 'pointer'}}
                  onClick={() => setShowConfirmShowPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
                {errors.confirmPassword && errors.confirmPassword && (
                  <div className='text-danger mt-2'>{errors.confirmPassword}</div>
                )}
              </div>
              <button type='submit' className='btn btn-primary d-block w-100'>
                Sign Up
              </button>
              <div className='text-center text-gray-500 mt-4'>
                Already have an Account? <Link to='/auth'>Sign in</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
